import { classes } from "polytype";
import {
  AggregateMetaData,
  DataModel,
  ListDataModel,
  ListQueryModel,
  ReferenceModel,
} from "../../base/index.js";
import { type AsyncResourceVariant, provideReact } from "../../react/index.js";
import { config } from "../../config/config.js";
import assertObjectFound from "../../base/assertObjectFound.js";
import type {
  BackupCreateExportRequestData,
  BackupCreateRequestData,
  BackupData,
  BackupListItemData,
  BackupListQueryData,
} from "./types.js";
import { Project } from "../../project/index.js";
import { DateTime } from "luxon";
import { BackupSchedule } from "../BackupSchedule/index.js";
import { BackupExport } from "./BackupExport.js";

export class Backup extends ReferenceModel {
  public static aggregateMetaData = new AggregateMetaData(
    "backup",
    "projectbackup",
  );

  public static ofId(id: string) {
    return new Backup(id);
  }

  public static find = provideReact(async (id: string) => {
    const data = await config.behaviors.backup.find(id);
    if (data !== undefined) {
      return new BackupDetailed(data);
    }
  });

  public static get = provideReact(async (id: string) => {
    const backup = await this.find(id);
    assertObjectFound(backup, "backup", id);
    return backup;
  });

  public findDetailed = provideReact(
    () => Backup.find(this.id),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<BackupDetailed | undefined>>;

  public getDetailed = provideReact(
    () => Backup.get(this.id),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<BackupDetailed>>;

  public static query(project: Project, query: BackupListQueryData = {}) {
    return new BackupListQuery(project, query);
  }

  public static async create(project: Project, data: BackupCreateRequestData) {
    const { id } = await config.behaviors.backup.create(project.id, data);
    return new Backup(id);
  }

  public async delete() {
    await config.behaviors.backup.delete(this.id);
  }

  public async updateDescription(description?: string) {
    await config.behaviors.backup.updateDescription(this.id, description);
  }

  public async createExport(data: BackupCreateExportRequestData) {
    await config.behaviors.backup.createExport(this.id, data);
  }
}

class BackupCommon extends classes(
  DataModel<BackupListItemData | BackupData>,
  Backup,
) {
  public readonly createdAt?: DateTime;
  public readonly requestedAt?: DateTime;
  public readonly expiresAt?: DateTime;
  public readonly description?: string;
  public readonly isDeletable: boolean;
  public readonly export?: BackupExport;
  public readonly isCompleted: boolean;
  public readonly schedule?: BackupSchedule;
  public readonly project: Project;
  public constructor(data: BackupListItemData | BackupData) {
    super([data], [data.id]);
    this.createdAt = data.createdAt
      ? DateTime.fromISO(data.createdAt)
      : undefined;
    this.requestedAt = data.requestedAt
      ? DateTime.fromISO(data.requestedAt)
      : undefined;
    this.expiresAt = data.expiresAt
      ? DateTime.fromISO(data.expiresAt)
      : undefined;
    this.description = data.description;
    this.isDeletable = data.deletable;
    this.export = data.export ? new BackupExport(data.export) : undefined;
    this.isCompleted = data.status === "Completed";
    this.schedule = data.parentId
      ? BackupSchedule.ofId(data.parentId)
      : undefined;
    this.project = Project.ofId(data.projectId);
  }
}

export class BackupDetailed extends classes(
  BackupCommon,
  DataModel<BackupData>,
) {
  public constructor(data: BackupData) {
    super([data], [data]);
  }
}

export class BackupListItem extends classes(
  BackupCommon,
  DataModel<BackupListItemData>,
) {
  public constructor(data: BackupListItemData) {
    super([data], [data]);
  }
}

export class BackupListQuery extends ListQueryModel<BackupListQueryData> {
  public readonly project: Project;

  public constructor(project: Project, query: BackupListQueryData = {}) {
    super(query, {
      dependencies: [project.id],
    });
    this.project = project;
  }

  public refine(query: BackupListQueryData) {
    return new BackupListQuery(this.project, {
      ...this.query,
      ...query,
    });
  }

  public execute = provideReact(async () => {
    const { items, totalCount } = await config.behaviors.backup.list(
      this.project.id,
      this.query,
    );
    return new BackupList(
      this.project,
      this.query,
      items.map((d) => new BackupListItem(d)),
      totalCount,
    );
  }, [this.queryId, "backup"]);

  public getTotalCount = provideReact(async () => {
    const { totalCount } = await this.refine({ limit: 1 }).execute();
    return totalCount;
  }, [this.queryId, "backup"]);
}

export class BackupList extends classes(
  BackupListQuery,
  ListDataModel<BackupListItem>,
) {
  public constructor(
    project: Project,
    query: BackupListQueryData,
    backups: BackupListItem[],
    totalCount: number,
  ) {
    super([project, query], [backups, totalCount]);
  }
}
